import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useSurveyStore } from '@/store/surveyStore'
import { useAnalysisStore } from '@/store/analysisStore'
import { useAuthStore } from '@/store/authStore'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { Button } from '@/components/ui/Button'
import { SkinConditionsSelector } from '@/components/survey/SkinConditionsSelector'
import { SKIN_TYPE_OPTIONS, SENSITIVITY_OPTIONS } from '@/types/survey'
import type { SurveyOption } from '@/types/survey'
import { haptic } from '@/lib/haptics'
import { trackFunnelEvent, trackEngagementEvent } from '@/services/analytics'

// Debug logging
console.log('[SurveyPage] Module loaded')

// Decorative sparkle component
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Background gradients for each step
const STEP_GRADIENTS = [
  'from-[#FFF5F0] via-[#FFFEF1] to-white', // Birth year - warm peach
  'from-[#F0F9FF] via-[#FFFEF1] to-white', // Skin type - cool blue
  'from-[#FFF0F5] via-[#FFFEF1] to-white', // Sensitivity - soft pink
  'from-[#F0FFF4] via-[#FFFEF1] to-white', // Conditions - fresh green
]

// Question configuration for each step (4 steps total)
const QUESTIONS = [
  { question: "What's your birth year?", subtitle: 'You must be 16 or older to use this service' },
  { question: "What's your skin type?", subtitle: 'How does your skin typically feel?' },
  { question: 'How sensitive is your skin?', subtitle: 'How does your skin react to new products?' },
  { question: '', subtitle: '' }, // SkinConditionsSelector has its own header
]

export function SurveyPage() {
  console.log('[SurveyPage] Component rendering...')

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { analysisResults } = useAnalysisStore()
  const { hasPaid, demoMode } = useAuthStore()

  // Auto-redirect users with existing results
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (analysisResults && !hasRedirected.current) {
      hasRedirected.current = true
      if (hasPaid || demoMode) {
        navigate('/report', { state: { showWelcomeToast: true } })
      } else {
        navigate('/teaser', { state: { showWelcomeToast: true } })
      }
    }
  }, [analysisResults, hasPaid, demoMode, navigate])

  // Track survey started on mount
  const hasTrackedStart = useRef(false)
  useEffect(() => {
    if (!hasTrackedStart.current && !analysisResults) {
      hasTrackedStart.current = true
      trackFunnelEvent.surveyStarted()
    }
  }, [analysisResults])

  console.log('[SurveyPage] Getting store...')

  // Get store with defensive destructuring
  const store = useSurveyStore()
  console.log('[SurveyPage] Raw store:', store)

  // Defensive: ensure all values exist with defaults
  const shortSurvey = store?.shortSurvey ?? { age: null, skinType: null, sensitivity: null, skinConditions: [] }
  const currentStep = typeof store?.currentStep === 'number' ? store.currentStep : 0
  const totalSteps = typeof store?.totalSteps === 'number' ? store.totalSteps : 4
  const setAge = store?.setAge ?? (() => {})
  const setSkinType = store?.setSkinType ?? (() => {})
  const setSensitivity = store?.setSensitivity ?? (() => {})
  const toggleSkinCondition = store?.toggleSkinCondition ?? (() => {})
  const nextStep = store?.nextStep ?? (() => {})
  const prevStep = store?.prevStep ?? (() => {})
  const goToStep = store?.goToStep ?? (() => {})
  const isCurrentStepValid = store?.isCurrentStepValid ?? (() => false)

  console.log('[SurveyPage] Store loaded, currentStep:', currentStep, 'shortSurvey:', shortSurvey)

  // Support URL param to jump to specific step
  useEffect(() => {
    const stepParam = searchParams.get('step')
    if (stepParam) {
      const step = parseInt(stepParam, 10)
      if (!isNaN(step) && step >= 0 && step < totalSteps) {
        goToStep(step)
      }
    }
  }, [searchParams, goToStep, totalSteps])

  const handleNext = () => {
    if (currentStep === totalSteps - 1) {
      // Track survey completion
      trackFunnelEvent.surveyCompleted({
        age: shortSurvey.age ?? 0,
        skin_type: shortSurvey.skinType ?? 'unknown',
        sensitivity: shortSurvey.sensitivity ?? 'unknown',
      })
      navigate('/photo')
    } else {
      nextStep()
    }
  }

  const handleBack = () => {
    if (currentStep === 0) {
      navigate('/home')
    } else {
      prevStep()
    }
  }

  // Defensive: ensure currentQuestion exists
  const safeStep = Math.max(0, Math.min(currentStep, QUESTIONS.length - 1))
  const currentQuestion = QUESTIONS[safeStep] ?? { question: '', subtitle: '' }
  console.log('[SurveyPage] currentQuestion:', currentQuestion)

  // Render options based on step - with defensive null checks
  const renderOptions = () => {
    console.log('[SurveyPage] renderOptions called, step:', currentStep)
    try {
      switch (currentStep) {
        case 0:
          return <BirthYearInput value={shortSurvey?.age ?? null} onChange={setAge} />
        case 1:
          return (
            <OptionsList options={SKIN_TYPE_OPTIONS} value={shortSurvey?.skinType ?? null} onChange={setSkinType} fieldName="skin_type" />
          )
        case 2:
          return (
            <OptionsList
              options={SENSITIVITY_OPTIONS}
              value={shortSurvey?.sensitivity ?? null}
              onChange={setSensitivity}
              fieldName="sensitivity"
            />
          )
        case 3:
          return (
            <SkinConditionsSelector
              selectedConditions={shortSurvey?.skinConditions ?? []}
              onToggle={toggleSkinCondition}
            />
          )
        default:
          return null
      }
    } catch (e) {
      console.error('[SurveyPage] renderOptions error:', e)
      return <div className="text-red-500 p-4">Error loading question</div>
    }
  }

  return (
    <div className={`h-dvh flex flex-col overflow-hidden bg-gradient-to-b ${STEP_GRADIENTS[safeStep]} relative`}>
      {/* Decorative sparkles - position changes with step */}
      <Sparkle className="absolute top-20 left-4 z-0" size={14} color="#FF8C33" delay={0} />
      <Sparkle className="absolute top-32 right-6 z-0" size={10} color="#F596FA" delay={0.5} />
      <Sparkle className="absolute bottom-32 left-8 z-0" size={12} color="#7182F8" delay={1} />

      {/* Fixed Header - Progress Bar */}
      <header className="shrink-0 bg-white/60 backdrop-blur-sm border-b border-gray-100/50 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <ProgressBar current={currentStep} total={totalSteps} />
        </div>
      </header>

      {/* Question + Options - Single AnimatePresence to prevent flicker */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="flex-1 min-h-0 flex flex-col overflow-hidden relative z-10"
        >
          {/* Question Section - only show if question text exists */}
          {currentQuestion.question && (
            <div className="shrink-0 px-4 pt-6 pb-4">
              <div className="max-w-md mx-auto text-center">
                <motion.h2
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-2xl sm:text-3xl font-bold text-[#075D61] mb-2"
                  style={{ fontFamily: "'Anybody', sans-serif" }}
                >
                  {currentQuestion.question}
                </motion.h2>
                {currentQuestion.subtitle && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="text-gray-500"
                  >
                    {currentQuestion.subtitle}
                  </motion.p>
                )}
              </div>
            </div>
          )}

          {/* Scrollable Options Section */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            <div className="max-w-md mx-auto">{renderOptions()}</div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Fixed Footer - Navigation Buttons */}
      <div className="shrink-0 bg-white/60 backdrop-blur-sm border-t border-gray-100/50 safe-bottom relative z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={handleBack} className="w-24 shrink-0">
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              size="lg"
              onClick={handleNext}
              disabled={!isCurrentStepValid()}
              className="flex-1 whitespace-nowrap shadow-md"
            >
              {currentStep === totalSteps - 1 ? 'Take Photo' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Birth year input with 4 individual digit boxes
function BirthYearInput({ value, onChange }: { value: number | null; onChange: (age: number) => void }) {
  // Convert stored age back to birth year for display
  const currentYear = new Date().getFullYear()
  const initialBirthYear = value ? (currentYear - value).toString() : ''

  const [digits, setDigits] = useState<string[]>(() => {
    if (initialBirthYear.length === 4) {
      return initialBirthYear.split('')
    }
    return ['', '', '', '']
  })

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  // Auto-focus first input on mount (delay for animation to complete)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Focus the input to show keyboard on mobile
      inputRefs[0].current?.focus()
    }, 300)  // Wait for page animation (200ms) + buffer
    return () => clearTimeout(timer)
  }, [])

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1)

    const newDigits = [...digits]
    newDigits[index] = digit
    setDigits(newDigits)

    // Calculate age if all 4 digits are entered
    const birthYearStr = newDigits.join('')
    if (birthYearStr.length === 4) {
      const birthYear = parseInt(birthYearStr, 10)
      const age = currentYear - birthYear
      if (age >= 16 && age <= 100) {
        onChange(age)
        trackEngagementEvent.surveyFieldChanged('age', age)
      }
    }

    // Auto-advance to next input
    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace - move to previous input if current is empty
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4)
    if (pasted.length === 4) {
      const newDigits = pasted.split('')
      setDigits(newDigits)
      inputRefs[3].current?.focus()

      const birthYear = parseInt(pasted, 10)
      const age = currentYear - birthYear
      if (age >= 16 && age <= 100) {
        onChange(age)
        trackEngagementEvent.surveyFieldChanged('age', age)
      }
    }
  }

  const birthYearStr = digits.join('')
  const isComplete = birthYearStr.length === 4
  const birthYear = isComplete ? parseInt(birthYearStr, 10) : null
  const calculatedAge = birthYear ? currentYear - birthYear : null
  const isValidAge = calculatedAge !== null && calculatedAge >= 16 && calculatedAge <= 100

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="pt-8 flex flex-col items-center"
    >
      <div className="flex gap-3" onPaste={handlePaste}>
        {[0, 1, 2, 3].map((index) => (
          <motion.input
            key={index}
            ref={inputRefs[index]}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digits[index]}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className={`
              w-16 h-20 text-center text-4xl font-bold rounded-2xl border-2 shadow-sm
              focus:outline-none focus:border-[#46C0C6] focus:shadow-md transition-all
              ${digits[index] ? 'border-[#46C0C6] bg-[#ECF9F9] text-[#075D61]' : 'border-gray-200 bg-white'}
            `}
            aria-label={`Birth year digit ${index + 1}`}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-6"
      >
        {isComplete && isValidAge ? (
          <motion.p
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="text-sm bg-[#ECF9F9] text-[#075D61] px-4 py-2 rounded-full font-medium"
          >
            You are <span className="font-bold text-[#46C0C6]">{calculatedAge}</span> years old
          </motion.p>
        ) : isComplete && !isValidAge ? (
          <p className="text-sm text-red-500 bg-red-50 px-4 py-2 rounded-full">
            Age must be between 16 and 100
          </p>
        ) : (
          <p className="text-sm text-gray-400">
            Enter your birth year (e.g., 1990)
          </p>
        )}
      </motion.div>
    </motion.div>
  )
}

// Reusable options list component - no staggered animation to prevent flickering
function OptionsList<T extends string | number>({
  options,
  value,
  onChange,
  fieldName,
}: {
  options: SurveyOption<T>[]
  value: T | null
  onChange: (value: T) => void
  fieldName?: string
}) {
  return (
    <div className="space-y-3 pt-2">
      {options.map((option) => {
        const isSelected = value === option.value
        return (
          <motion.button
            key={String(option.value)}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => {
              haptic('selection')
              onChange(option.value)
              if (fieldName) {
                trackEngagementEvent.surveyFieldChanged(fieldName, String(option.value))
              }
            }}
            className={`
              w-full p-4 rounded-2xl border-2 text-left transition-all duration-200
              active:scale-[0.98] transform shadow-sm
              ${
                isSelected
                  ? 'border-[#46C0C6] bg-[#ECF9F9] shadow-md'
                  : 'border-gray-100 hover:border-[#46C0C6]/50 hover:shadow-md bg-white'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <motion.div
                animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.2 }}
                className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all
                  ${isSelected ? 'border-[#46C0C6] bg-[#46C0C6]' : 'border-gray-300'}
                `}
              >
                {isSelected && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
              </motion.div>
              <div>
                <span className={`font-medium ${isSelected ? 'text-[#075D61]' : 'text-gray-900'}`}>
                  {option.label}
                </span>
                {option.description && (
                  <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                )}
              </div>
            </div>
          </motion.button>
        )
      })}
    </div>
  )
}
